import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './StudentsModule.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 12;

const StudentsModule = ({ school }) => {
  const { t } = useTranslation('landing');
  const sm = (key) => t(`business.schoolDashboard.studentsModule.${key}`);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  const [schoolClasses, setSchoolClasses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState('existing'); // 'existing' | 'new'
  const [addForm, setAddForm] = useState({
    userId: '',
    classId: '',
    academicYear: '',
    studentNumber: '',
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    stream: '',
    phone: '',
    email: ''
  });
  const [saving, setSaving] = useState(false);

  const getToken = () => sessionStorage.getItem('token') || localStorage.getItem('token');

  const fetchClasses = useCallback(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE_URL}/api/schools/classes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => d.success && setSchoolClasses(d.classes || []));
  }, []);

  const fetchStudents = useCallback(() => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    if (classFilter) params.set('class_id', classFilter);
    if (yearFilter.trim()) params.set('academic_year', yearFilter.trim());
    if (statusFilter) params.set('status', statusFilter);
    if (search.trim()) params.set('search', search.trim());
    if (sortBy) params.set('sort', sortBy === 'attendance' ? 'attendance' : sortBy === 'payment' ? 'payment' : 'name');
    fetch(`${API_BASE_URL}/api/schools/students?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.students) setStudents(data.students);
        else setError(data.message || 'Failed to load students');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [classFilter, yearFilter, statusFilter, search, sortBy]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const openProfile = (s) => {
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE_URL}/api/schools/students/${s.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json())
      .then((data) => data.success && setSelectedStudent(data.student))
      .catch(() => setError('Failed to load profile'));
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    if (addMode === 'existing' && !addForm.userId.trim()) return;
    if (addMode === 'new' && (!addForm.firstName.trim() || !addForm.lastName.trim())) return;
    setSaving(true);
    fetch(`${API_BASE_URL}/api/schools/students`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ...(addMode === 'existing' ? { userId: addForm.userId.trim() } : {}),
        classId: addForm.classId || undefined,
        academicYear: addForm.academicYear.trim() || undefined,
        studentNumber: addForm.studentNumber.trim() || undefined,
        stream: addForm.stream.trim() || undefined,
        firstName: addMode === 'new' ? addForm.firstName.trim() : undefined,
        lastName: addMode === 'new' ? addForm.lastName.trim() : undefined,
        gender: addMode === 'new' ? (addForm.gender || undefined) : undefined,
        dateOfBirth: addMode === 'new' ? (addForm.dateOfBirth || undefined) : undefined,
        phone: addMode === 'new' ? (addForm.phone.trim() || undefined) : undefined,
        email: addMode === 'new' ? (addForm.email.trim() || undefined) : undefined
      })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setShowAddModal(false);
          setAddMode('existing');
          setAddForm({
            userId: '',
            classId: '',
            academicYear: '',
            studentNumber: '',
            firstName: '',
            lastName: '',
            gender: '',
            dateOfBirth: '',
            stream: '',
            phone: '',
            email: ''
          });
          fetchStudents();
        } else setError(data.message || 'Failed to enroll');
      })
      .catch((err) => setError(err.message))
      .finally(() => setSaving(false));
  };

  const badgeClass = (s) => {
    if (s.isRepeater) return 'repeater';
    if (s.lastPaymentStatus === 'pending' || s.lastPaymentStatus === 'partial') return 'payment-pending';
    if (s.attendanceRate30 != null && s.attendanceRate30 < 70) return 'low-attendance';
    if (s.attendanceRate30 != null && s.attendanceRate30 >= 90) return 'excellent';
    return '';
  };

  const paginated = students.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="students-module">
      <div className="students-module-toolbar">
        <div className="students-module-search">
          <span className="material-icons">search</span>
          <input
            type="text"
            placeholder={sm('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="students-module-filters">
          <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
            <option value="">{sm('allClasses')}</option>
            {schoolClasses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{sm('allStatuses')}</option>
            <option value="active">{sm('active')}</option>
            <option value="graduated">{sm('graduated')}</option>
            <option value="transferred">{sm('transferred')}</option>
            <option value="withdrawn">{sm('withdrawn')}</option>
          </select>
          <input
            type="text"
            placeholder={sm('academicYear')}
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="students-module-year-input"
          />
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="name">{sm('sortName')}</option>
          <option value="attendance">{sm('sortAttendance')}</option>
          <option value="payment">{sm('sortPayment')}</option>
        </select>
        <div className="students-module-view-toggle">
          <button type="button" className={viewMode === 'cards' ? 'active' : ''} onClick={() => setViewMode('cards')} title={sm('cardView')}><span className="material-icons">grid_view</span></button>
          <button type="button" className={viewMode === 'table' ? 'active' : ''} onClick={() => setViewMode('table')} title={sm('tableView')}><span className="material-icons">table_rows</span></button>
        </div>
        <button
          type="button"
          className="students-module-add-btn"
          onClick={() => {
            setShowAddModal(true);
            setAddMode('existing');
            setAddForm({
              userId: '',
              classId: '',
              academicYear: '',
              studentNumber: '',
              firstName: '',
              lastName: '',
              gender: '',
              dateOfBirth: '',
              stream: '',
              phone: '',
              email: ''
            });
          }}
        >
          <span className="material-icons">person_add</span> {sm('addStudent')}
        </button>
      </div>

      {error && (
        <div className="students-module-error">
          <span className="material-icons">error_outline</span> {error}
        </div>
      )}

      {loading ? (
        <div className="students-module-loading">
          <span className="material-icons spin">refresh</span>
          <p>{sm('loading')}</p>
        </div>
      ) : (
        <>
          <div className="students-module-content">
            {students.length === 0 ? (
              <p className="students-module-empty">{sm('noStudentsYet')}</p>
            ) : viewMode === 'table' ? (
              <div className="students-module-table-wrap">
                <table className="students-module-table">
                  <thead>
                    <tr>
                      <th>{sm('studentNumber')}</th>
                      <th>{sm('name')}</th>
                      <th>{sm('class')}</th>
                      <th>{sm('attendance')}</th>
                      <th>{sm('paymentStatus')}</th>
                      <th>{sm('status')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((s) => (
                      <tr key={s.id}>
                        <td>{s.studentNumber || '—'}</td>
                        <td>{s.fullName}</td>
                        <td>{s.className || '—'}</td>
                        <td>{s.attendanceRate30 != null ? `${s.attendanceRate30}%` : '—'}</td>
                        <td><span className={`students-module-pay students-module-pay-${s.lastPaymentStatus || 'none'}`}>{s.lastPaymentStatus || '—'}</span></td>
                        <td><span className={`students-module-status students-module-status-${s.status}`}>{s.status}</span>{s.isRepeater && <span className="students-module-badge repeater">{sm('repeater')}</span>}</td>
                        <td><button type="button" className="students-module-btn-sm" onClick={() => openProfile(s)}>{sm('view')}</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="students-module-cards">
                {paginated.map((s) => (
                  <div key={s.id} className="students-module-card">
                    {badgeClass(s) && <span className={`students-module-badge ${badgeClass(s)}`}>{badgeClass(s) === 'repeater' ? sm('repeater') : badgeClass(s) === 'payment-pending' ? sm('paymentPending') : badgeClass(s) === 'low-attendance' ? sm('lowAttendance') : sm('excellent')}</span>}
                    <div className="students-module-card-avatar"><span className="material-icons">person</span></div>
                    <h3 className="students-module-card-name">{s.fullName}</h3>
                    <p className="students-module-card-meta">{s.className || '—'} · {s.academicYear || '—'}</p>
                    <p className="students-module-card-id">{sm('studentNumber')}: {s.studentNumber || '—'}</p>
                    <div className="students-module-card-stats">
                      {s.attendanceRate30 != null && <span><span className="material-icons">event_available</span> {s.attendanceRate30}%</span>}
                      {s.lastPaymentStatus && <span className={`students-module-pay students-module-pay-${s.lastPaymentStatus}`}>{s.lastPaymentStatus}</span>}
                    </div>
                    <div className="students-module-card-actions">
                      <button type="button" onClick={() => openProfile(s)}><span className="material-icons">visibility</span> {sm('view')}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {students.length > PAGE_SIZE && (
            <div className="students-module-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{sm('previous')}</button>
              <span>{sm('page')} {page} of {Math.ceil(students.length / PAGE_SIZE)}</span>
              <button type="button" disabled={page >= Math.ceil(students.length / PAGE_SIZE)} onClick={() => setPage((p) => p + 1)}>{sm('next')}</button>
            </div>
          )}
        </>
      )}

      {showAddModal && (
        <div className="students-module-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="students-module-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{sm('addStudent')}</h3>
            <div className="students-module-add-tabs" role="tablist" aria-label={sm('addStudent')}>
              <button
                type="button"
                className={addMode === 'existing' ? 'active' : ''}
                onClick={() => setAddMode('existing')}
              >
                {sm('enrollExistingUser')}
              </button>
              <button
                type="button"
                className={addMode === 'new' ? 'active' : ''}
                onClick={() => setAddMode('new')}
              >
                {sm('createNewStudent')}
              </button>
            </div>
            <p className="students-module-hint">{addMode === 'existing' ? sm('addStudentHintExisting') : sm('addStudentHintNew')}</p>
            <form onSubmit={handleAddSubmit}>
              <div className="students-module-form-grid">
                {addMode === 'existing' && (
                  <div className="students-module-form-row">
                    <label>{sm('userId')} *</label>
                    <input
                      value={addForm.userId}
                      onChange={(e) => setAddForm((p) => ({ ...p, userId: e.target.value }))}
                      placeholder="User ID"
                      required
                    />
                  </div>
                )}

                {addMode === 'new' && (
                  <>
                    <div className="students-module-form-row">
                      <label>{sm('firstName')} *</label>
                      <input value={addForm.firstName} onChange={(e) => setAddForm((p) => ({ ...p, firstName: e.target.value }))} required />
                    </div>
                    <div className="students-module-form-row">
                      <label>{sm('lastName')} *</label>
                      <input value={addForm.lastName} onChange={(e) => setAddForm((p) => ({ ...p, lastName: e.target.value }))} required />
                    </div>
                    <div className="students-module-form-row">
                      <label>{sm('gender')}</label>
                      <select value={addForm.gender} onChange={(e) => setAddForm((p) => ({ ...p, gender: e.target.value }))}>
                        <option value="">{sm('select')}</option>
                        <option value="male">{sm('male')}</option>
                        <option value="female">{sm('female')}</option>
                        <option value="other">{sm('other')}</option>
                      </select>
                    </div>
                    <div className="students-module-form-row">
                      <label>{sm('dateOfBirth')}</label>
                      <input type="date" value={addForm.dateOfBirth} onChange={(e) => setAddForm((p) => ({ ...p, dateOfBirth: e.target.value }))} />
                    </div>
                    <div className="students-module-form-row">
                      <label>{sm('specialization')}</label>
                      <input value={addForm.stream} onChange={(e) => setAddForm((p) => ({ ...p, stream: e.target.value }))} />
                    </div>
                    <div className="students-module-form-row">
                      <label>{sm('phone')}</label>
                      <input value={addForm.phone} onChange={(e) => setAddForm((p) => ({ ...p, phone: e.target.value }))} />
                    </div>
                    <div className="students-module-form-row">
                      <label>{sm('email')}</label>
                      <input type="email" value={addForm.email} onChange={(e) => setAddForm((p) => ({ ...p, email: e.target.value }))} />
                    </div>
                  </>
                )}

                <div className="students-module-form-row">
                  <label>{sm('class')}</label>
                  <select value={addForm.classId} onChange={(e) => setAddForm((p) => ({ ...p, classId: e.target.value }))}>
                    <option value="">—</option>
                    {schoolClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="students-module-form-row">
                  <label>{sm('academicYear')}</label>
                  <input value={addForm.academicYear} onChange={(e) => setAddForm((p) => ({ ...p, academicYear: e.target.value }))} placeholder="2024-2025" />
                </div>
                <div className="students-module-form-row">
                  <label>{sm('studentNumber')}</label>
                  <input value={addForm.studentNumber} onChange={(e) => setAddForm((p) => ({ ...p, studentNumber: e.target.value }))} />
                </div>
              </div>
              <div className="students-module-modal-actions">
                <button type="button" onClick={() => setShowAddModal(false)}>{sm('cancel')}</button>
                <button type="submit" disabled={saving}>{saving ? '…' : addMode === 'existing' ? sm('enroll') : sm('create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedStudent && (
        <div className="students-module-modal-overlay" onClick={() => setSelectedStudent(null)}>
          <div className="students-module-modal students-module-profile-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedStudent.fullName}</h3>
            <div className="students-module-profile-section">
              <p><strong>{sm('class')}:</strong> {selectedStudent.className || '—'}</p>
              <p><strong>{sm('studentNumber')}:</strong> {selectedStudent.studentNumber || '—'}</p>
              <p><strong>{sm('specialization')}:</strong> {selectedStudent.stream || '—'}</p>
              <p><strong>{sm('academicYear')}:</strong> {selectedStudent.academicYear || '—'}</p>
              <p><strong>{sm('status')}:</strong> <span className={`students-module-status students-module-status-${selectedStudent.status}`}>{selectedStudent.status}</span></p>
              <p><strong>{sm('gender')}:</strong> {selectedStudent.gender || '—'}</p>
              <p><strong>{sm('dateOfBirth')}:</strong> {selectedStudent.dateOfBirth || '—'}</p>
              <p><strong>{sm('email')}:</strong> {selectedStudent.email}</p>
              <p><strong>{sm('phone')}:</strong> {selectedStudent.phone || '—'}</p>
              {selectedStudent.attendanceRate30 != null && <p><strong>{sm('attendance')} (30d):</strong> {selectedStudent.attendanceRate30}%</p>}
              {selectedStudent.lastPaymentStatus && <p><strong>{sm('lastPayment')}:</strong> <span className={`students-module-pay students-module-pay-${selectedStudent.lastPaymentStatus}`}>{selectedStudent.lastPaymentStatus}</span></p>}
            </div>
            <div className="students-module-modal-actions">
              <button type="button" onClick={() => setSelectedStudent(null)}>{sm('close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsModule;
